import React, { useEffect, useState, useRef } from 'react';
import FullCalendar from '@fullcalendar/react';
import dayGridPlugin from '@fullcalendar/daygrid';
import timeGridPlugin from '@fullcalendar/timegrid';
import interactionPlugin from '@fullcalendar/interaction';
import { visitAPI, partyAPI } from '../../services/api';
import { useAuth } from '../../context/AuthContext';

// Same overlap rule the backend's double-booking guard enforces
// (start < other.end AND end > other.start, terminal states ignored):
// flag any pair of same-clinician visits that intersect so schedulers see
// conflicts the moment they appear on the grid.
const TERMINAL = new Set(['COMPLETED', 'CANCELLED', 'NO_SHOW', 'RESCHEDULED']);

function findConflicts(visits) {
  const conflicted = new Set();
  const byNurse = {};
  for (const v of visits) {
    if (TERMINAL.has(v.visit_status)) continue;
    (byNurse[v.nurse_id] = byNurse[v.nurse_id] || []).push(v);
  }
  for (const group of Object.values(byNurse)) {
    for (let i = 0; i < group.length; i++) {
      for (let j = i + 1; j < group.length; j++) {
        const a = group[i], b = group[j];
        if (new Date(a.scheduled_start) < new Date(b.scheduled_end) &&
            new Date(a.scheduled_end) > new Date(b.scheduled_start)) {
          conflicted.add(a.visit_id);
          conflicted.add(b.visit_id);
        }
      }
    }
  }
  return conflicted;
}

const SERVICE_COLORS = {
  NURSE_VISIT:        '#7b1e3c',
  DOCTOR_ASSESSMENT:  '#1e4a7b',
  PHYSIOTHERAPY:      '#1e7b4a',
  WOUND_CARE:         '#7b6c1e',
  PALLIATIVE:         '#6c1e7b',
};

const EVV_BADGE = {
  COMPLIANT:     { bg: '#dcfce7', color: '#166534' },
  NON_COMPLIANT: { bg: '#fee2e2', color: '#991b1b' },
  PENDING:       { bg: '#fef9c3', color: '#92400e' },
  EXEMPT:        { bg: '#e0e7ff', color: '#3730a3' },
};

function evtColor(visit) {
  const base = SERVICE_COLORS[visit.visit_type] || '#7b1e3c';
  if (visit.visit_series_id) return base + 'cc'; // slightly transparent for recurring
  return base;
}

export default function VisitCalendar() {
  const { user } = useAuth();
  const roles = user?.roles || [];
  const isAdmin = roles.includes('ADMIN');
  const isNurse = roles.includes('NURSE') && !isAdmin;

  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState(null);
  const [clinicians, setClinicians] = useState([]);
  const [clinicianId, setClinicianId] = useState('');
  const [conflictCount, setConflictCount] = useState(0);
  const calRef = useRef(null);
  // Remember the visible range so changing the clinician filter reloads it.
  const rangeRef = useRef({ start: null, end: null });

  // Clinician picker (admins/doctors plan other people's days; a nurse's
  // own list is already self-scoped by the backend, so no picker needed).
  useEffect(() => {
    if (isNurse) return;
    partyAPI.list({ role: 'NURSE', per_page: 200 })
      .then(r => setClinicians(r.data || []))
      .catch(() => setClinicians([]));
  }, [isNurse]);

  async function loadVisits(startStr, endStr, nurseId = clinicianId) {
    rangeRef.current = { start: startStr, end: endStr };
    setLoading(true);
    try {
      const resp = await visitAPI.list({
        date_from: startStr?.split('T')[0],
        date_to: endStr?.split('T')[0],
        ...(nurseId ? { nurse_id: nurseId } : {}),
        per_page: 200,
      });
      // visitAPI.list already unwraps the axios layer: `resp` IS the API
      // envelope, so the rows live at resp.data (the old resp.data.data
      // never matched — the calendar silently rendered empty).
      const visits = Array.isArray(resp?.data) ? resp.data : (resp?.data?.data || []);
      const conflicts = findConflicts(visits);
      setConflictCount(conflicts.size);
      setEvents(visits.map(v => {
        const inConflict = conflicts.has(v.visit_id);
        return {
          id: v.visit_id,
          title: `${v.patient_name || 'Patient'} — ${v.clinician_name || 'Nurse'}`,
          start: v.scheduled_start,
          end: v.scheduled_end || v.scheduled_start,
          backgroundColor: inConflict ? '#dc2626' : evtColor(v),
          borderColor: inConflict ? '#7f1d1d' : evtColor(v),
          textColor: '#fff',
          extendedProps: { ...v, _conflict: inConflict },
        };
      }));
    } catch {
    } finally {
      setLoading(false);
    }
  }

  function handleDatesSet(info) {
    loadVisits(info.startStr, info.endStr);
  }

  function handleClinicianChange(e) {
    const id = e.target.value;
    setClinicianId(id);
    const { start, end } = rangeRef.current;
    if (start) loadVisits(start, end, id);
  }

  function handleEventClick(info) {
    setSelected(info.event.extendedProps);
  }

  const statusColor = {
    SCHEDULED: '#dbeafe', NURSE_EN_ROUTE: '#fef9c3', IN_PROGRESS: '#d1fae5',
    COMPLETED: '#dcfce7', CANCELLED: '#fee2e2',
  };

  return (
    <div style={{ padding: '24px 28px', fontFamily: 'Inter, sans-serif' }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 20 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 14, flexWrap: 'wrap' }}>
          <h1 style={{ fontSize: 22, fontWeight: 800, color: '#2b1b22', margin: 0 }}>Visit Calendar</h1>
          {!isNurse && (
            <select
              value={clinicianId}
              onChange={handleClinicianChange}
              aria-label="Filter by clinician"
              style={{
                padding: '7px 12px', borderRadius: 8, border: '1px solid #ece3e7',
                fontSize: 13, color: '#2b1b22', background: '#fff', fontWeight: 600,
                cursor: 'pointer',
              }}
            >
              <option value="">All clinicians</option>
              {clinicians.map(c => (
                <option key={c.party_id} value={c.party_id}>{c.name}</option>
              ))}
            </select>
          )}
          {conflictCount > 0 && (
            <span style={{
              padding: '5px 12px', borderRadius: 20, background: '#fee2e2',
              color: '#991b1b', fontSize: 12, fontWeight: 700,
            }}>
              ⚠ {conflictCount} overlapping visit{conflictCount > 1 ? 's' : ''}
            </span>
          )}
        </div>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          {Object.entries(SERVICE_COLORS).map(([type, color]) => (
            <span key={type} style={{
              padding: '3px 10px', borderRadius: 20, background: color,
              color: '#fff', fontSize: 11, fontWeight: 600,
            }}>{type.replace('_', ' ')}</span>
          ))}
          <span style={{ padding: '3px 10px', borderRadius: 20, background: '#e5e7eb',
            color: '#6b7280', fontSize: 11, fontWeight: 600, border: '2px dashed #9ca3af' }}>
            Recurring (translucent)
          </span>
        </div>
      </div>

      {loading && (
        <div style={{ textAlign: 'center', padding: 8, color: '#7b1e3c', fontSize: 13 }}>
          Loading visits…
        </div>
      )}

      <div style={{ background: '#fff', borderRadius: 12, border: '1px solid #ece3e7',
        padding: 16, boxShadow: '0 1px 4px rgba(0,0,0,0.06)' }}>
        <FullCalendar
          ref={calRef}
          plugins={[dayGridPlugin, timeGridPlugin, interactionPlugin]}
          initialView="timeGridWeek"
          headerToolbar={{
            left: 'prev,next today',
            center: 'title',
            right: 'dayGridMonth,timeGridWeek,timeGridDay',
          }}
          height="auto"
          events={events}
          datesSet={handleDatesSet}
          eventClick={handleEventClick}
          slotMinTime="06:00:00"
          slotMaxTime="22:00:00"
          allDaySlot={false}
          nowIndicator
          eventContent={(info) => (
            <div style={{ padding: '2px 4px', fontSize: 11, overflow: 'hidden' }}>
              <div style={{ fontWeight: 700, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                {info.event.title}
              </div>
              <div style={{ opacity: 0.85 }}>
                {info.event.extendedProps._conflict && '⚠ '}
                {info.event.extendedProps.visit_type?.replace('_', ' ')}
                {info.event.extendedProps.visit_series_id && ' 🔁'}
              </div>
            </div>
          )}
        />
      </div>

      {/* Detail panel */}
      {selected && (
        <div style={{
          position: 'fixed', right: 0, top: 0, bottom: 0, width: 340,
          background: '#fff', borderLeft: '1px solid #ece3e7',
          boxShadow: '-4px 0 20px rgba(0,0,0,0.12)', zIndex: 100,
          overflowY: 'auto', padding: 24,
        }}>
          <button onClick={() => setSelected(null)} style={{
            position: 'absolute', top: 16, right: 16, background: 'none',
            border: 'none', fontSize: 20, cursor: 'pointer', color: '#7b1e3c',
          }}>✕</button>

          <h2 style={{ fontSize: 17, fontWeight: 800, color: '#2b1b22', marginBottom: 16, marginTop: 0 }}>
            Visit Details
          </h2>

          {selected._conflict && (
            <div style={{ marginBottom: 14, padding: '8px 12px', background: '#fee2e2',
              borderRadius: 8, fontSize: 12.5, color: '#991b1b', fontWeight: 600 }}>
              ⚠ This visit overlaps another visit for the same clinician.
              Reschedule one of them to resolve the conflict.
            </div>
          )}

          <Field label="Patient" value={selected.patient_name || selected.patient_id} />
          <Field label="Clinician" value={selected.clinician_name || selected.nurse_id} />
          <Field label="Type" value={selected.visit_type} />
          <Field label="Status">
            <span style={{ padding: '2px 8px', borderRadius: 20, fontSize: 12, fontWeight: 600,
              background: statusColor[selected.visit_status] || '#f3f4f6', color: '#374151' }}>
              {selected.visit_status}
            </span>
          </Field>
          <Field label="Scheduled" value={new Date(selected.scheduled_start).toLocaleString()} />
          {selected.checkin_time && (
            <Field label="Checked In" value={new Date(selected.checkin_time).toLocaleString()} />
          )}
          {selected.checkout_time && (
            <Field label="Checked Out" value={new Date(selected.checkout_time).toLocaleString()} />
          )}
          {selected.actual_duration_min && (
            <Field label="Duration" value={`${selected.actual_duration_min} min`} />
          )}

          {/* EVV badge */}
          {selected.evv_status && (
            <div style={{ marginTop: 16 }}>
              <div style={{ fontSize: 11, fontWeight: 700, color: '#5e4a53',
                textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 4 }}>
                EVV Status
              </div>
              <span style={{
                padding: '3px 12px', borderRadius: 20, fontSize: 12, fontWeight: 700,
                ...(EVV_BADGE[selected.evv_status] || { bg: '#f3f4f6', color: '#374151' }),
                background: (EVV_BADGE[selected.evv_status] || {}).bg,
              }}>
                {selected.evv_status}
              </span>
            </div>
          )}

          {selected.visit_series_id && (
            <div style={{ marginTop: 16, padding: '8px 12px', background: '#f0f9ff',
              borderRadius: 8, fontSize: 12, color: '#0369a1' }}>
              🔁 Part of a recurring series
            </div>
          )}

          {selected.checkin_code && (
            <div style={{ marginTop: 16 }}>
              <Field label="Check-in Code" value={selected.checkin_code} />
              <Field label="Check-out Code" value={selected.checkout_code} />
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function Field({ label, value, children }) {
  return (
    <div style={{ marginBottom: 12 }}>
      <div style={{ fontSize: 11, fontWeight: 700, color: '#5e4a53',
        textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 2 }}>
        {label}
      </div>
      <div style={{ fontSize: 13, color: '#2b1b22' }}>{children || value || '—'}</div>
    </div>
  );
}
